import { useState, useEffect, useRef } from "react";
import { Target } from "lucide-react";
import { GRADES } from '../constants';
import PageHeader from '../components/PageHeader';
import SiteFooter from '../components/SiteFooter';
import SignupGateModal from '../components/SignupGateModal';

/* ─── Swipe-to-delete hook ─────────────────────────────────────────── */
function useSwipe(onSwipeLeft, onSwipeRight) {
  const startX = useRef(null);

  const onTouchStart = (e) => {
    startX.current = e.touches[0].clientX;
  };

  const onTouchEnd = (e) => {
    if (startX.current === null) return;
    const delta = startX.current - e.changedTouches[0].clientX;
    if (delta > 60) onSwipeLeft();
    else if (delta < -30) onSwipeRight();
    startX.current = null;
  };

  return { onTouchStart, onTouchEnd };
}

/* ─── Mobile watchlist card ────────────────────────────────────────── */
function MobileWatchlistCard({ item, alertTicker, alertVal, setAlertTicker, setAlertVal, setAlert, remove, revealOpen, setRevealOpen }) {
  const fv = item.fairValue;
  const cp = item.currentPrice;
  const upside = fv && cp ? ((fv - cp) / cp * 100) : null;
  const isUp = upside != null && upside >= 0;
  const gradeColor = GRADES[item.grade]?.color || "#e8c875";

  /* value bar: position of current price between 0 and fair value */
  const barPct = fv && cp ? Math.min(100, (cp / fv) * 100) : null;
  const barColor = isUp ? "#00C805" : "#FF3B30";

  const swipe = useSwipe(
    () => setRevealOpen(item.ticker),
    () => { if (revealOpen === item.ticker) setRevealOpen(null); }
  );

  return (
    <div
      {...swipe}
      style={{ position: "relative", overflow: "hidden", borderRadius: 12, marginBottom: 10 }}
      onClick={() => { if (revealOpen === item.ticker) setRevealOpen(null); }}
    >
      {/* Main card */}
      <div
        style={{
          background: "rgba(255,255,255,0.03)",
          border: "1px solid rgba(201,168,76,0.13)",
          borderRadius: 12,
          padding: "14px 16px",
          transform: revealOpen === item.ticker ? "translateX(-80px)" : "translateX(0)",
          transition: "transform 0.22s ease",
          position: "relative",
          zIndex: 1,
        }}
      >
        {/* Row 1: ticker + company + grade */}
        <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
          <span style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 22, fontWeight: 900, color: "#ffe87a", flex: "0 0 auto", minWidth: 52 }}>
            {item.ticker}
          </span>
          <span style={{ flex: 1, fontSize: 13, color: "rgba(255,255,255,0.55)", overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
            {item.companyName}
          </span>
          {item.grade && (
            <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 6, background: GRADES[item.grade]?.bg || "rgba(201,168,76,0.1)", color: gradeColor, border: `1px solid ${gradeColor}40`, flexShrink: 0 }}>
              {item.grade}
            </span>
          )}
        </div>

        {/* Row 2: price → fair value + upside */}
        <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 10, flexWrap: "wrap" }}>
          <span style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 16, fontWeight: 700, color: "#fff" }}>
            {cp ? `$${cp.toFixed(2)}` : "—"}
          </span>
          <span style={{ fontSize: 13, color: "rgba(255,255,255,0.35)" }}>→</span>
          <span style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 16, fontWeight: 700, color: isUp ? "#00C805" : "#FF3B30" }}>
            {fv ? `$${fv.toFixed(2)}` : "—"}
          </span>
          {upside != null && (
            <span style={{
              marginLeft: "auto",
              fontFamily: "'Rajdhani',sans-serif",
              fontSize: 16, fontWeight: 800,
              color: isUp ? "#00C805" : "#FF3B30",
            }}>
              {upside >= 0 ? "+" : ""}{upside.toFixed(1)}%
            </span>
          )}
          {item.alertPrice && !item.alertTriggered && (
            <span style={{ fontSize: 10, color: "#e8c875", background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 6, padding: "2px 7px" }}>
              Alert @ ${item.alertPrice}
            </span>
          )}
          {item.alertTriggered && (
            <span style={{ fontSize: 10, color: "#00C805", background: "rgba(0,200,5,0.08)", border: "1px solid rgba(0,200,5,0.2)", borderRadius: 6, padding: "2px 7px" }}>
              Alert triggered
            </span>
          )}
        </div>

        {/* Row 3: value bar */}
        {barPct !== null && (
          <div style={{ marginBottom: 12 }}>
            <div style={{ height: 5, borderRadius: 3, background: "rgba(255,255,255,0.07)", overflow: "hidden" }}>
              <div style={{ width: `${barPct}%`, height: "100%", background: barColor, borderRadius: 3, transition: "width 0.4s ease" }} />
            </div>
            <div style={{ display: "flex", justifyContent: "space-between", marginTop: 3 }}>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)" }}>$0</span>
              <span style={{ fontSize: 11, color: "rgba(255,255,255,0.25)" }}>${fv.toFixed(0)} fair value</span>
            </div>
          </div>
        )}

        {/* Row 4: action buttons */}
        {alertTicker === item.ticker ? (
          <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
            <input
              type="number"
              value={alertVal}
              onChange={e => setAlertVal(e.target.value)}
              placeholder="Alert $"
              style={{ flex: 1, minHeight: 44, padding: "5px 10px", fontSize: 14, background: "#1a1a1a", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 8, color: "#fff", outline: "none" }}
            />
            <button
              onClick={() => setAlert(item.ticker, alertVal)}
              style={{ minHeight: 44, padding: "5px 14px", fontSize: 13, fontWeight: 700, background: "rgba(201,168,76,0.15)", color: "#e8c875", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 8, cursor: "pointer" }}>
              Set
            </button>
            <button
              onClick={() => { setAlertTicker(null); setAlertVal(""); }}
              style={{ minHeight: 44, padding: "5px 10px", fontSize: 13, background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>
              ✕
            </button>
          </div>
        ) : (
          <div style={{ display: "flex", gap: 7 }}>
            <button
              onClick={e => { e.stopPropagation(); setAlertTicker(item.ticker); setAlertVal(item.alertPrice || ""); }}
              style={{ flex: 1, minHeight: 44, fontSize: 12, fontWeight: 600, background: "rgba(201,168,76,0.07)", color: "#e8c875", border: "1px solid rgba(201,168,76,0.18)", borderRadius: 8, cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", gap: 4 }}>
              <Target size={12} /> Alert
            </button>
            <a
              href={`/?t=${item.ticker}`}
              onClick={e => e.stopPropagation()}
              style={{ flex: 1, minHeight: 44, fontSize: 12, fontWeight: 600, background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, textDecoration: "none", display: "flex", alignItems: "center", justifyContent: "center" }}>
              Analyze
            </a>
            <button
              onClick={e => { e.stopPropagation(); remove(item.ticker); }}
              style={{ flex: 1, minHeight: 44, fontSize: 12, background: "rgba(255,59,48,0.07)", color: "#FF3B30", border: "1px solid rgba(255,59,48,0.15)", borderRadius: 8, cursor: "pointer", fontWeight: 700 }}>
              Remove
            </button>
          </div>
        )}
      </div>

      {/* Swipe-reveal remove zone */}
      <div
        onClick={e => { e.stopPropagation(); remove(item.ticker); }}
        style={{
          position: "absolute",
          right: 0, top: 0, bottom: 0,
          width: 80,
          background: "#FF3B30",
          borderRadius: "0 12px 12px 0",
          display: "flex",
          alignItems: "center",
          justifyContent: "center",
          zIndex: 0,
          cursor: "pointer",
        }}>
        <span style={{ color: "#fff", fontSize: 12, fontWeight: 800, letterSpacing: ".04em" }}>Remove</span>
      </div>
    </div>
  );
}

/* ─── Page ─────────────────────────────────────────────────────────── */
export default function WatchlistPage() {
  const [items,       setItems]       = useState([]);
  const [loading,     setLoading]     = useState(true);
  const [alertTicker, setAlertTicker] = useState(null);
  const [alertVal,    setAlertVal]    = useState("");
  const [revealOpen,  setRevealOpen]  = useState(null); // ticker with swipe reveal open
  const session = (() => { try { return localStorage.getItem("hma_session") || null; } catch { return null; } })();
  const isMobile = typeof window !== "undefined" && window.innerWidth <= 768;

  useEffect(() => {
    if (!session) { setLoading(false); return; }
    fetch(`/api/watchlist?session=${session}`)
      .then(r => r.json()).then(d => { setItems(d.items || []); setLoading(false); }).catch(() => setLoading(false));
  }, [session]);

  const remove = async (ticker) => {
    await fetch("/api/watchlist", { method: "DELETE", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session, ticker }) });
    setItems(prev => prev.filter(i => i.ticker !== ticker));
    setRevealOpen(null);
  };
  const setAlert = async (ticker, price) => {
    const r = await fetch("/api/watchlist", { method: "PATCH", headers: { "Content-Type": "application/json" }, body: JSON.stringify({ session, ticker, alertPrice: price }) });
    const d = await r.json();
    setItems(d.items || []);
    setAlertTicker(null); setAlertVal("");
  };

  return (
    <div style={{ fontFamily: "'Inter',sans-serif", background: "#000", minHeight: "100vh", color: "#fff" }}>

      {/* ── Mobile-only styles ── */}
      <style>{`
        /* Desktop rows visible by default, mobile cards hidden */
        .wl-desktop-row  { display: flex; }
        .wl-mobile-card  { display: none; }

        @media (max-width: 768px) {
          .wl-desktop-row { display: none !important; }
          .wl-mobile-card { display: block !important; }
        }
      `}</style>

      <PageHeader active="watchlist" />
      <div style={{ maxWidth: 900, margin: "0 auto", padding: "32px 24px 80px" }}>
        <div style={{ marginBottom: 28 }}>
          <div className="hma-page-label">◆ Watchlist</div>
          <div className="hma-page-title">Your Tracked Stocks</div>
          <div style={{ fontSize: 13, color: "rgba(255,255,255,0.5)", marginTop: 4 }}>Monitor fair value vs. current price. Set alerts to get emailed when a stock hits your target.</div>
        </div>

        {!session && !isMobile && <SignupGateModal onClose={() => window.location.href = "/"} onLogin={() => window.location.reload()} />}
        {session && loading && <div style={{ color: "rgba(255,255,255,0.4)", fontSize: 14 }}>Loading…</div>}

        {(session || isMobile) && !loading && items.length === 0 && (
          <div style={{ background: "rgba(255,255,255,0.03)", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "28px 24px", textAlign: "center" }}>
            <div style={{ fontSize: 15, fontWeight: 600, color: "rgba(255,255,255,0.5)", marginBottom: 8 }}>Your watchlist is empty</div>
            <div style={{ fontSize: 13, color: "rgba(255,255,255,0.3)", marginBottom: 16 }}>After running an analysis, click "+ Watchlist" to add stocks here.</div>
            <a href="/" style={{ display: "inline-block", padding: "10px 24px", background: "linear-gradient(135deg,#e8c875,#ffe87a)", color: "#000", fontWeight: 700, borderRadius: 10, textDecoration: "none", fontSize: 13 }}>Run an Analysis →</a>
          </div>
        )}

        {(session || isMobile) && !loading && items.length > 0 && (
          <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
            {items.map(item => {
              const fv = item.fairValue;
              const cp = item.currentPrice;
              const upside = fv && cp ? ((fv - cp) / cp * 100) : null;
              const isUp = upside != null && upside >= 0;
              const gradeColor = GRADES[item.grade]?.color || "#e8c875";

              return (
                <div key={item.ticker} style={{ marginBottom: 10 }}>

                  {/* ── Desktop row (unchanged) ── */}
                  <div className="wl-desktop-row" style={{ background: "#0F0F0F", border: "1px solid rgba(255,255,255,0.08)", borderRadius: 14, padding: "16px 18px", gap: 14, alignItems: "center", flexWrap: "wrap" }}>
                    <div style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 26, fontWeight: 900, color: "#ffe87a", minWidth: 60 }}>{item.ticker}</div>
                    <div style={{ flex: 1, minWidth: 120 }}>
                      <div style={{ fontSize: 13, color: "rgba(255,255,255,0.6)", marginBottom: 3 }}>{item.companyName}</div>
                      <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center" }}>
                        {item.grade && <span style={{ fontSize: 11, fontWeight: 800, padding: "2px 8px", borderRadius: 6, background: GRADES[item.grade]?.bg || "rgba(201,168,76,0.1)", color: gradeColor, border: `1px solid ${gradeColor}40` }}>{item.grade}</span>}
                        {item.alertPrice && !item.alertTriggered && <span style={{ fontSize: 10, color: "#e8c875", background: "rgba(201,168,76,0.08)", border: "1px solid rgba(201,168,76,0.2)", borderRadius: 6, padding: "2px 8px" }}>Alert @ ${item.alertPrice}</span>}
                        {item.alertTriggered && <span style={{ fontSize: 10, color: "#00C805", background: "rgba(0,200,5,0.08)", border: "1px solid rgba(0,200,5,0.2)", borderRadius: 6, padding: "2px 8px" }}>Alert triggered</span>}
                      </div>
                    </div>
                    <div style={{ textAlign: "center", minWidth: 70 }}>
                      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".1em", color: "rgba(255,255,255,0.4)", marginBottom: 2 }}>Current</div>
                      <div style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 18, fontWeight: 700, color: "#fff" }}>{cp ? `$${cp.toFixed(2)}` : "—"}</div>
                    </div>
                    <div style={{ textAlign: "center", minWidth: 70 }}>
                      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".1em", color: "rgba(255,255,255,0.4)", marginBottom: 2 }}>Fair Value</div>
                      <div style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 18, fontWeight: 700, color: isUp ? "#00C805" : "#FF3B30" }}>{fv ? `$${fv.toFixed(2)}` : "—"}</div>
                      {(item.fairValueLow && item.fairValueHigh) && <div style={{ fontSize: 11, color: "rgba(255,255,255,0.3)" }}>${item.fairValueLow.toFixed(0)}–${item.fairValueHigh.toFixed(0)}</div>}
                    </div>
                    <div style={{ textAlign: "center", minWidth: 60 }}>
                      <div style={{ fontSize: 11, textTransform: "uppercase", letterSpacing: ".1em", color: "rgba(255,255,255,0.4)", marginBottom: 2 }}>Upside</div>
                      <div style={{ fontFamily: "'Rajdhani',sans-serif", fontSize: 18, fontWeight: 700, color: isUp ? "#00C805" : "#FF3B30" }}>{upside != null ? `${upside >= 0 ? "+" : ""}${upside.toFixed(1)}%` : "—"}</div>
                    </div>
                    <div style={{ display: "flex", gap: 6, flexShrink: 0 }}>
                      {alertTicker === item.ticker ? (
                        <div style={{ display: "flex", gap: 5, alignItems: "center" }}>
                          <input type="number" value={alertVal} onChange={e => setAlertVal(e.target.value)}
                            placeholder="Alert $" style={{ width: 80, padding: "5px 8px", fontSize: 12, background: "#1a1a1a", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 8, color: "#fff", outline: "none" }} />
                          <button onClick={() => setAlert(item.ticker, alertVal)} style={{ padding: "5px 10px", minHeight: 44, fontSize: 11, fontWeight: 700, background: "rgba(201,168,76,0.15)", color: "#e8c875", border: "1px solid rgba(201,168,76,0.3)", borderRadius: 8, cursor: "pointer" }}>Set</button>
                          <button onClick={() => { setAlertTicker(null); setAlertVal(""); }} style={{ padding: "5px 8px", minHeight: 44, fontSize: 11, background: "none", border: "none", color: "rgba(255,255,255,0.4)", cursor: "pointer" }}>✕</button>
                        </div>
                      ) : (
                        <>
                          <button onClick={() => { setAlertTicker(item.ticker); setAlertVal(item.alertPrice || ""); }} style={{ padding: "5px 12px", minHeight: 44, fontSize: 11, fontWeight: 600, background: "rgba(201,168,76,0.07)", color: "#e8c875", border: "1px solid rgba(201,168,76,0.18)", borderRadius: 8, cursor: "pointer" }}><Target size={12} style={{ verticalAlign: "-2px", marginRight: 3 }} />Alert</button>
                          <a href={`/?t=${item.ticker}`} style={{ padding: "5px 12px", minHeight: 44, fontSize: 11, fontWeight: 600, background: "rgba(255,255,255,0.05)", color: "rgba(255,255,255,0.6)", border: "1px solid rgba(255,255,255,0.1)", borderRadius: 8, textDecoration: "none", display: "inline-flex", alignItems: "center" }}>Analyze</a>
                          <button onClick={() => remove(item.ticker)} style={{ padding: "5px 10px", minHeight: 44, fontSize: 11, background: "rgba(255,59,48,0.07)", color: "#FF3B30", border: "1px solid rgba(255,59,48,0.15)", borderRadius: 8, cursor: "pointer" }}>×</button>
                        </>
                      )}
                    </div>
                  </div>

                  {/* ── Mobile card ── */}
                  <div className="wl-mobile-card">
                    <MobileWatchlistCard
                      item={item}
                      alertTicker={alertTicker}
                      alertVal={alertVal}
                      setAlertTicker={setAlertTicker}
                      setAlertVal={setAlertVal}
                      setAlert={setAlert}
                      remove={remove}
                      revealOpen={revealOpen}
                      setRevealOpen={setRevealOpen}
                    />
                  </div>

                </div>
              );
            })}
          </div>
        )}
      </div>
      <SiteFooter />
    </div>
  );
}
